import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useState } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { RejectInvoice } from "src/screens/creditors/creditor/details/actions/rejectInvoice/RejectInvoice";
import { ApproveInvoice } from "src/screens/creditors/creditor/details/actions/approveInvoice/ApproveInvoice";
import { StartWorkflow } from "src/screens/creditors/creditor/details/actions/startWorkflow/StartWorkflow";
import { INVOICE_STATE, LOADING_TYPE } from "src/stores/krediflow/InvoiceEnums";
import styles from "./InvoiceWorkflowTab.module.css";
import { CancelInvoice } from "src/screens/creditors/creditor/details/actions/cancelInvoice/CancelInvoice";
import { RestartInvoice } from "src/screens/creditors/creditor/details/actions/restartInvoice/RestartInvoice";
import { useHistory } from "react-router-dom";
import { Route, selectRoute } from "src/config/routes";
import { MessageType } from "src/components/notifications/Notifier";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { DeleteInvoice } from "src/screens/creditors/creditor/details/actions/deleteInvoice/DeleteInvoice";

const InvoiceActionTabBase = () => {
    const { t } = useTranslation();
    const { invoiceStore, uiStore, authStore, invoiceListStore } = useContext(RootStoreContext);

    const history = useHistory();

    const [isModalDisplayed, setIsModalDisplayed] = useState<boolean>(false);
    const [isDeleteInvoiceMenuPointClicked, setIsDeleteInvoiceMenuPointClicked] = useState<boolean>(false);
    const [displaSecondaryMobileActions, setDisplaySecondaryMobileActions] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState(false);

    const onDeleteInvoiceMenuPoints = useCallback(() => {
        setIsDeleteInvoiceMenuPointClicked(!isDeleteInvoiceMenuPointClicked);
    }, [isDeleteInvoiceMenuPointClicked]);

    const onClickDisplaySecondaryMobileActions = useCallback(() => {
        setDisplaySecondaryMobileActions(!displaSecondaryMobileActions);
    }, [displaSecondaryMobileActions]);

    const onCloseDeleteInvoiceModal = useCallback(() => {
        setIsModalDisplayed(false);
        setIsDeleteInvoiceMenuPointClicked(false);
    }, []);

    const onClickDeleteInvoice = useCallback(async () => {
        if (isLoading) return;
        setIsLoading(true);

        const successfullyAbortedInvoiceProcess = await invoiceStore.currentInvoice?.abortInvoiceProcess();

        if (successfullyAbortedInvoiceProcess) {
            uiStore.printStatusMessage(t("screens.kredi_flow.action.abort_invoice.success"), MessageType.SUCCESS);

            if (invoiceListStore.isBatchProcessing) {
                invoiceListStore.navigateToNextBatchProcess();
            } else {
                history.replace(selectRoute(Route.creditors, authStore.user?.role));
            }
        } else {
            uiStore.printStatusMessage(t("screens.kredi_flow.action.abort_invoice.failed"), MessageType.ERROR);
        }

        setIsLoading(false);
        setIsModalDisplayed(false);
        setIsDeleteInvoiceMenuPointClicked(false);
    }, [uiStore, invoiceStore, t, history, authStore, isLoading, invoiceListStore]);

    const onClickCancelDeleteButton = useCallback(() => {
        setIsModalDisplayed(true);
    }, []);

    const deleteInvoiceMenuPointsAndModal = (
        <>
            <div className={styles.MenuPoint} onClick={onDeleteInvoiceMenuPoints}>
                ...
            </div>
            <UI.Modal size="lg" show={isModalDisplayed} centered={true} className={styles.MenuPointModal}>
                <div className={styles.ModalTitle}>{t("screens.tickets.action.archive.cancel")}</div>
                <div>{t("screens.kredi_flow.action.abort_invoice.confirmation_text")}</div>
                <div className={"d-flex justify-content-end mt-2"}>
                    <UI.Button
                        label={t("labels.cancel")}
                        className={styles.ModalButtonCancel}
                        onClick={onCloseDeleteInvoiceModal}
                        variant="outline-primary"
                    />
                    <UI.Button
                        label={
                            isLoading === true ? (
                                <UI.RotatingSpinner className={styles.LoadSpinner} size={20} noLogo />
                            ) : (
                                t("screens.kredi_flow.action.abort_invoice.cancel_invoice")
                            )
                        }
                        className={styles.ModalButtonDelete}
                        onClick={onClickDeleteInvoice}
                        disabled={isLoading}
                        variant="danger"
                    />
                </div>
            </UI.Modal>
        </>
    );

    const deleteInvoiceButton = (
        <UI.Button
            className={"mb-2"}
            label={t("screens.tickets.action.archive.cancel")}
            hidden={uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? false : !isDeleteInvoiceMenuPointClicked}
            onClick={onClickCancelDeleteButton}
            variant="danger"
        />
    );

    const desktopElements = (
        <>
            {invoiceStore.currentInvoice?.realestateRequiredToContinueWorkflow && (
                <span className="error-text">
                    {t("screens.kredi_flow.action.realestate_required_to_start_workflow")}
                </span>
            )}
            {invoiceStore.currentInvoice?.canCurrentUserSeeStartWorkflowButton && (
                <div className="mr-2">
                    <StartWorkflow />
                </div>
            )}
            <div className="mr-2">
                <ApproveInvoice />
            </div>
            <div className="mr-2">
                <RejectInvoice />
            </div>
            <div className="mr-2">
                <RestartInvoice />
            </div>
            <div className="mr-2">
                <CancelInvoice />
            </div>
            <div className="mr-2">
                <DeleteInvoice />
            </div>
            <div className="mr-2">{deleteInvoiceButton}</div>
            {invoiceStore.currentInvoice?.state &&
                invoiceStore.currentInvoice?.state < INVOICE_STATE.READY_FOR_VISA &&
                deleteInvoiceMenuPointsAndModal}
        </>
    );

    const mobileActionsPrimary = (
        <>
            {invoiceStore.currentInvoice?.realestateRequiredToContinueWorkflow && (
                <span className="error-text">
                    {t("screens.kredi_flow.action.realestate_required_to_start_workflow")}
                </span>
            )}
            {invoiceStore.currentInvoice?.canCurrentUserSeeStartWorkflowButton && (
                <div className="mr-2 w-100">
                    <StartWorkflow />
                </div>
            )}
            {invoiceStore.currentInvoice?.canCurrentUserApproveOrReject && (
                <div className="mr-2 w-100">
                    <ApproveInvoice />
                </div>
            )}
            {invoiceStore.currentInvoice?.canCurrentUserRestartOrCancel && (
                <div className="mr-2 w-100">
                    <RestartInvoice />
                </div>
            )}
        </>
    );

    const mobileSecondaryActionsMenuPoints = (
        <div className={styles.MenuPoint} onClick={onClickDisplaySecondaryMobileActions}>
            ...
        </div>
    );

    const mobileActionsSecondary = (
        <>
            <div className="mr-2">
                <RejectInvoice />
            </div>
            <div className="mr-2">
                <CancelInvoice />
            </div>
            {invoiceStore.currentInvoice?.state &&
                invoiceStore.currentInvoice?.state < INVOICE_STATE.READY_FOR_VISA &&
                deleteInvoiceButton}
        </>
    );

    const mobileElements = (
        <div className={`${styles.MobileActionContainer} mobile-action-container w-100 d-flex position-relative`}>
            <div className={`${styles.MobileActionPrimaryContainer} mobile-action-primary-container w-100 d-flex`}>
                {mobileActionsPrimary}
                {mobileSecondaryActionsMenuPoints}
            </div>
            {displaSecondaryMobileActions && (
                <div className={styles.MobileActionsSecondaryContainer}>{mobileActionsSecondary}</div>
            )}
        </div>
    );

    const actionElements = uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? mobileElements : desktopElements;

    return (
        <div
            className={`${styles.InvoiceActionTabContainer} d-flex ${
                invoiceStore.loadingType === LOADING_TYPE.INITIALIZING
                    ? "justify-content-center"
                    : "justify-content-end"
            }`}
        >
            {invoiceStore.loadingType === LOADING_TYPE.INITIALIZING ? (
                <div className={styles.LoadingContainer}>
                    <UI.RotatingSpinner noLogo size={25} className={styles.LoadingTicketSpinner} />
                    <div className="ml-2">{t("screens.kredi_flow.loading_actions")}</div>
                </div>
            ) : (
                actionElements
            )}
        </div>
    );
};

export const InvoiceActionTab = Sentry.withProfiler(observer(InvoiceActionTabBase));
